// ----- Phase 5.X-publish-governance — Modal #1: Pre-publish checklist -----
//
// Operator must check every manual item AND type their initials before
// continuing. Initials are an acknowledgment-of-meaning per 21 CFR
// Part 11 §11.50; the checklist itself is the auditable evidence.
//
// Items are exactly what GET /prepublish-checklist served (T-17 T17-D14: the
// web declares no list of its own): the manual sign-off items plus the
// `auto-detected` items (Phase 5.X-prepublish-checklist-autodetect). Auto items render
// with a ✅/❌ icon (— when unknown / not run) and cannot be toggled; the operator cannot bypass
// a failing required auto item by checking a box. The server
// re-evaluates auto items on POST /compile and rejects with 422
// PUBLISH_BLOCKED_CHECKLIST_AUTO if any required auto item still
// fails — frontend rendering is informational only.

import { useState, type ReactElement } from 'react';
import { ModalShell } from './modalShell';
import {
  buttonRowStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
} from './modalShellStyles';
import { type ChecklistItem } from '@/lib/compiler/publishGovernanceFlow';

export interface PrePublishChecklistModalProps {
  /** The items the server served (auto evaluated first, then manual). */
  readonly items: ReadonlyArray<ChecklistItem>;
  readonly onContinue: (acks: Record<string, boolean>) => void;
  readonly onCancel: () => void;
  /**
   * When true, the Continue button reads "Proceed to Sign & Publish →"
   * (matches V1 ReviewScreen when `requireReauthAtPublish` is on).
   * Defaults to false → "Proceed to Publish →".
   */
  readonly requiresReauth?: boolean;
  /**
   * T-17 (T17-D3): when `validation-passed` is unknown (never run) the row offers
   * "Run validation now". The parent runs validation and re-serves `items`.
   */
  readonly onRunValidation?: () => void;
  readonly validationRunning?: boolean;
  readonly validationRunError?: string | null;
}

export function PrePublishChecklistModal({
  items,
  onContinue,
  onCancel,
  requiresReauth = false,
  onRunValidation,
  validationRunning = false,
  validationRunError = null,
}: PrePublishChecklistModalProps): ReactElement {
  // Only manual items go into the ack map; auto items are evaluated
  // server-side and the operator can't toggle them.
  const manualItems = items.filter((i) => i.kind === 'manual');
  const autoItems = items.filter((i) => i.kind === 'auto');

  const [checks, setChecks] = useState<Record<string, boolean>>(
    () => Object.fromEntries(manualItems.map((i) => [i.id, false])),
  );
  const [initials, setInitials] = useState('');

  const allManualChecked = manualItems.every((i) => checks[i.id] === true);
  const initialsOk = initials.trim().length >= 2;
  // T-17 (T17-D3): a required auto item blocks unless it is a TRUE pass —
  // "did not run" (unknown) is not a pass.
  const failingRequiredAuto = autoItems.filter(
    (i) => i.required && i.passed !== true,
  );
  const autoGateOk = failingRequiredAuto.length === 0;
  const canContinue = allManualChecked && initialsOk && autoGateOk;

  return (
    <ModalShell testId="pgov-checklist-modal" title="Pre-publish checklist">
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
        This checklist has two sections: <strong>system-verified checks</strong> (computed
        automatically from submission state) and <strong>your sign-off</strong> (items only
        a human reviewer can confirm). Both are captured in the audit log.
      </p>
      {autoItems.length > 0 && (
        <>
          {/* Phase phase-review-surface-strengthen — clear section header */}
          <div
            data-testid="pgov-checklist-auto-section-header"
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              color: '#6B7280',
              marginTop: 4,
              marginBottom: 6,
            }}
          >
            System-verified (live state — cannot be overridden by checkbox)
          </div>
        </>
      )}
      {autoItems.length > 0 && (
        <ul
          data-testid="pgov-checklist-auto-list"
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {autoItems.map((item) => {
            const failed = item.passed === false;
            const unknown = item.passed !== true && !failed;
            const required = item.required;
            return (
              <li
                key={item.id}
                data-testid={`pgov-checklist-auto-${item.id}`}
                data-passed={item.passed === true ? 'true' : 'false'}
                data-state={unknown ? 'unknown' : failed ? 'fail' : 'pass'}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  fontSize: 13,
                  color: failed && required ? 'var(--danger, #b91c1c)' : 'inherit',
                  opacity: failed && !required ? 0.7 : 1,
                }}
              >
                <span
                  aria-label={unknown ? 'not run' : item.passed ? 'passed' : 'failed'}
                  style={{ marginTop: 2 }}
                >
                  {unknown ? '—' : item.passed ? '✅' : '❌'}
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span>
                    {item.label}
                    {!required && (
                      <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.7 }}>
                        (warning)
                      </span>
                    )}
                  </span>
                  {/* V1 parity: show "Auto-verified ✓" for passing items,
                      or the failureMessage for failing items — matches
                      V1 ReviewScreen's per-item detail sub-text. */}
                  {unknown && (
                    <span
                      data-testid={`pgov-checklist-auto-${item.id}-status`}
                      style={{ fontSize: 11, color: 'var(--text-muted)' }}
                    >
                      {item.id === 'validation-passed'
                        ? 'Not run yet'
                        : (item.detail ?? 'Not evaluated yet')}
                    </span>
                  )}
                  {unknown && item.id === 'validation-passed' && onRunValidation && (
                    <>
                      <button
                        type="button"
                        data-testid="pgov-checklist-run-validation"
                        onClick={onRunValidation}
                        disabled={validationRunning}
                        style={{ ...secondaryButtonStyle, alignSelf: 'flex-start' }}
                      >
                        {validationRunning ? 'Running validation…' : 'Run validation now'}
                      </button>
                      {validationRunError && (
                        <span role="alert" style={{ fontSize: 11, color: 'var(--danger, #b91c1c)' }}>
                          {validationRunError}
                        </span>
                      )}
                    </>
                  )}
                  {item.passed === true && (
                    <span
                      data-testid={`pgov-checklist-auto-${item.id}-status`}
                      style={{ fontSize: 11, color: 'var(--success, #15803d)' }}
                    >
                      Auto-verified ✓
                    </span>
                  )}
                  {failed && item.failureMessage && (
                    <span
                      data-testid={`pgov-checklist-auto-${item.id}-message`}
                      style={{ fontSize: 11 }}
                    >
                      {item.failureMessage}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {/* Phase phase-review-surface-strengthen — manual attestation section header */}
      {manualItems.length > 0 && (
        <div
          data-testid="pgov-checklist-manual-section-header"
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: '#6B7280',
            marginTop: autoItems.length > 0 ? 6 : 0,
            marginBottom: 6,
          }}
        >
          Your sign-off (human judgment required — tick each after reviewing)
        </div>
      )}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {manualItems.map((item) => (
          <li key={item.id}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                data-testid={`pgov-checklist-item-${item.id}`}
                checked={checks[item.id] === true}
                onChange={(e) => setChecks({ ...checks, [item.id]: e.target.checked })}
                style={{ marginTop: 3 }}
              />
              <span>{item.label}</span>
            </label>
          </li>
        ))}
      </ul>
      <label style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
        Your initials (recorded in audit log):
        <input
          type="text"
          data-testid="pgov-checklist-initials"
          value={initials}
          onChange={(e) => setInitials(e.target.value)}
          maxLength={5}
          style={{
            padding: 6,
            border: '1px solid var(--border)',
            borderRadius: 4,
            fontFamily: 'inherit',
            fontSize: 13,
            width: 100,
          }}
        />
      </label>
      <div style={buttonRowStyle}>
        <button
          type="button"
          data-testid="pgov-checklist-cancel"
          onClick={onCancel}
          style={secondaryButtonStyle}
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="pgov-checklist-continue"
          disabled={!canContinue}
          onClick={() => onContinue(checks)}
          style={{
            ...primaryButtonStyle,
            opacity: canContinue ? 1 : 0.5,
            cursor: canContinue ? 'pointer' : 'not-allowed',
          }}
        >
          {requiresReauth ? 'Proceed to Sign & Publish →' : 'Proceed to Publish →'}
        </button>
      </div>
    </ModalShell>
  );
}
